import { createContext, useContext } from 'react';
import type { ToothStatusType } from './types';

// ── Condition style type ───────────────────────────────────────────────────────

export interface ConditionStyle {
  crown: string;
  stroke: string;
  root?: string;
}

// ── Nordic-clinical palette — warm cream base, harmonious conditions ──────────
// Saturation level: 60–70%, lightness: 88–96% for fills; 45–55% for strokes

export const DEFAULT_DENTAL_PALETTE: Partial<Record<ToothStatusType, ConditionStyle>> = {
  caries:             { crown: '#fde8e8', stroke: '#e05252',  root: '#fef1f1' },
  filled:             { crown: '#dce8fc', stroke: '#4b8fe8' },
  crown:              { crown: '#fef4d3', stroke: '#d4a017' },
  implant:            { crown: '#eae5fd', stroke: '#8b6ce8',  root: '#f3f0fe' },
  root_canal:         { crown: '#fff8f0', stroke: '#e07a28',  root: '#fff3e6' },
  bridge:             { crown: '#e4f9f5', stroke: '#1ea898' },
  extraction_planned: { crown: '#fbd9d9', stroke: '#c83030' },
  fracture:           { crown: '#fde8e8', stroke: '#b52a2a' },
  periapical_abscess: { crown: '#fce8fd', stroke: '#b838c8',  root: '#fce8fd' },
  veneer:             { crown: '#e0f9fc', stroke: '#0fa8bc' },
  post_and_core:      { crown: '#e4faea', stroke: '#28a056' },
};

export const DEFAULT_STYLE: ConditionStyle = { crown: '#faf9f7', stroke: '#d6d3d1' };
export const HEALTHY_STYLE: ConditionStyle = { crown: '#fffefb', stroke: '#e7e5e4' };

// ── Procedure-status overlay tokens ──────────────────────────────────────────

export const PROCEDURE_STATUS: Record<
  'planned' | 'done_own' | 'done_other' | 'in_progress',
  { text: string; bg: string; border: string; hex: string; label: string }
> = {
  planned: {
    text:   'text-amber-700',
    bg:     'bg-amber-50',
    border: 'border-amber-300',
    hex:    '#d97706',
    label:  'Planlagt',
  },
  in_progress: {
    text:   'text-blue-700',
    bg:     'bg-blue-50',
    border: 'border-blue-300',
    hex:    '#2563eb',
    label:  'Under behandling',
  },
  done_own: {
    text:   'text-slate-700',
    bg:     'bg-slate-50',
    border: 'border-slate-300',
    hex:    '#475569',
    label:  'Udført (klinikken)',
  },
  done_other: {
    text:   'text-violet-700',
    bg:     'bg-violet-50',
    border: 'border-violet-300',
    hex:    '#7c3aed',
    label:  'Udført (anden klinik)',
  },
};

// ── Context ───────────────────────────────────────────────────────────────────

export const DentalColorContext = createContext<Partial<Record<ToothStatusType, ConditionStyle>>>(
  DEFAULT_DENTAL_PALETTE,
);

export function useDentalPalette(): Partial<Record<ToothStatusType, ConditionStyle>> {
  return useContext(DentalColorContext);
}

/**
 * Resolve the style for a given condition type.
 * Falls back to DEFAULT_STYLE for unknown conditions, HEALTHY_STYLE for 'healthy'.
 */
export function resolveConditionStyle(
  condition: ToothStatusType,
  palette: Partial<Record<ToothStatusType, ConditionStyle>>,
): ConditionStyle {
  if (condition === 'healthy') return HEALTHY_STYLE;
  return palette[condition] ?? DEFAULT_STYLE;
}

/**
 * Merge a clinic's JSONB dental_chart_colors over the default palette.
 * Only known ToothStatusType keys are applied.
 */
export function mergeClinicColors(
  clinicColors: Record<string, unknown> | null | undefined,
): Partial<Record<ToothStatusType, ConditionStyle>> {
  if (!clinicColors || Object.keys(clinicColors).length === 0) {
    return DEFAULT_DENTAL_PALETTE;
  }
  const merged: Partial<Record<ToothStatusType, ConditionStyle>> = { ...DEFAULT_DENTAL_PALETTE };
  for (const [key, val] of Object.entries(clinicColors)) {
    if (val && typeof val === 'object' && 'crown' in val && 'stroke' in val) {
      merged[key as ToothStatusType] = val as ConditionStyle;
    }
  }
  return merged;
}
